import React, { useState } from "react";
import { DashboardEditorProps } from "components/ComponentType";
import DatasetChartConfig from "components/basic/dataset-chart-config/DatasetChartConfig";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import DatasetMultiSelect from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import InputLabelWrapper from "components/basic/input-label-wrapper/InputLabelWrapper";
import { Divider } from "primereact/divider";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import {
  DatasetScatterProps,
  ImageConfig,
  YAxisIdentifier,
  YAxisIdentifierWithOverriddenTitle,
} from "../interfaces";
import ChartIdentifiersEditor from "../ChartIdentifiersEditor";

const DatasetScatterChartEditor: React.FC<DashboardEditorProps<DatasetScatterProps>> = props => {
  const { config, onConfigChange } = props;
  const [isIdentifiersEditDialogVisible, setIsIdentifiersEditDialogVisible] =
    useState<boolean>(false);

  const yAxisIdentifierValue: YAxisIdentifier[] = Array.isArray(config?.yAxisIdentifier)
    ? config?.yAxisIdentifier
    : [config?.yAxisIdentifier];

  const handleChange = (
    field: keyof DatasetScatterProps,
    value: string | string[] | number | boolean | null | YAxisIdentifier[]
  ) => {
    if (value === null) {
      const { [field]: remove, ...rest } = config;
      onConfigChange({ ...(rest as DatasetScatterProps) });
      return;
    }
    onConfigChange({ ...config, [field]: value });
  };

  const handleAxisConfigChange = (
    field: "xRange" | "yRange",
    place: "from" | "to",
    value: number | null
  ) => {
    const range = config?.axisConfig?.[field] || [];
    range[place === "from" ? 0 : 1] = value!;
    onConfigChange({ ...config, axisConfig: { ...config?.axisConfig, [field]: range } });
  };

  const handleImageConfigChange = (field: keyof ImageConfig, value: string | number | null) => {
    if ((value === null || value === "") && config.imageConfig) {
      const { [field]: remove, ...rest } = config.imageConfig;
      onConfigChange({ ...config, imageConfig: { ...(rest as ImageConfig) } });
      return;
    }
    onConfigChange({ ...config, imageConfig: { ...config?.imageConfig!, [field]: value } });
  };

  const isYAxisIdentifierWithOverride = (
    item: YAxisIdentifier
  ): item is YAxisIdentifierWithOverriddenTitle =>
    typeof item === "object" && item !== null && "identifier" in item;

  return (
    <div className="grid p-fluid">
      <DatasetChartConfig config={config} onConfigChange={onConfigChange} />
      <div className="flex flex-row justify-content-between w-full">
        <InputLabelWrapper labelName={T("label.scatter.x-axis-identifier")}>
          <DatasetDropdown
            value={config?.xAxisIdentifier}
            onChange={value => handleChange("xAxisIdentifier", value)}
          />
        </InputLabelWrapper>
        <InputLabelWrapper
          labelName={T("label.scatter.trend-lines")}
          col={4}
          className="align-items-left gap-3"
        >
          <InputSwitch
            checked={config?.trendLines}
            onChange={e => handleChange("trendLines", e.value)}
          />
        </InputLabelWrapper>
      </div>
      <InputLabelWrapper labelName={T("label.scatter.y-axis-identifiers")} col={12}>
        <div className="flex flex-row">
          <DatasetMultiSelect
            value={(yAxisIdentifierValue || [])
              .map(item => (isYAxisIdentifierWithOverride(item) ? item.identifier : item))
              .filter(item => item !== undefined)}
            onChange={selectedIdentifiers => {
              const existingIdentifiersMap = new Map(
                (yAxisIdentifierValue || []).map(item => [
                  isYAxisIdentifierWithOverride(item) ? item.identifier : item,
                  isYAxisIdentifierWithOverride(item) ? item.overriddenTitle : "",
                ])
              );
              const updatedIdentifiers = selectedIdentifiers.map(identifier => ({
                identifier,
                overriddenTitle: existingIdentifiersMap.get(identifier) || "",
              }));

              handleChange("yAxisIdentifier", updatedIdentifiers);
            }}
          />

          <Button
            icon="pi pi-pencil"
            type="button"
            className="p-button-secondary"
            tooltip={T("action.edit-identifiers")}
            onClick={() => setIsIdentifiersEditDialogVisible(true)}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scatter.color-axis-identifier")}>
        <DatasetDropdown
          value={config?.colorAxisIdentifier}
          onChange={value => handleChange("colorAxisIdentifier", value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scatter.size-axis-identifier")}>
        <DatasetDropdown
          value={config?.sizeAxisIdentifier}
          onChange={value => handleChange("sizeAxisIdentifier", value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scatter.x-axis-range")} col={3}>
        <div className="p-inputgroup">
          <InputNumber
            value={config?.axisConfig?.xRange?.[0]}
            onChange={e => handleAxisConfigChange("xRange", "from", e.value)}
            prefix="from "
            maxFractionDigits={5}
          />
          <InputNumber
            value={config?.axisConfig?.xRange?.[1]}
            onChange={e => handleAxisConfigChange("xRange", "to", e.value)}
            prefix="to "
            maxFractionDigits={5}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scatter.y-axis-range")} col={3}>
        <div className="p-inputgroup">
          <InputNumber
            value={config?.axisConfig?.yRange?.[0]}
            onChange={e => handleAxisConfigChange("yRange", "from", e.value)}
            prefix={`${T("word.from")} `}
            maxFractionDigits={5}
          />
          <InputNumber
            value={config?.axisConfig?.yRange?.[1]}
            onChange={e => handleAxisConfigChange("yRange", "to", e.value)}
            prefix={`${T("word.to")} `}
            maxFractionDigits={5}
          />
        </div>
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.scatter.highlight-count")} col={2}>
        <InputNumber
          value={config?.highlightCount}
          onChange={e => handleChange("highlightCount", e.value)}
          showButtons
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("label.highlighted-values-description")} col={4}>
            <InputText
              value={config?.highlightedValuesDescription}
              onChange={e => handleChange("highlightedValuesDescription", e.target.value)}
            />
      </InputLabelWrapper>
      <Divider />
      <h6 className="w-full m-0">{T("label.scatter.image-configuration")}:</h6>
      <InputLabelWrapper labelName={T("label.scatter.image-source")}>
        <InputText
          value={config?.imageConfig?.source ?? ""}
          onChange={e => handleImageConfigChange("source", e.target.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="X" col={1}>
        <InputNumber
          value={config?.imageConfig?.x}
          onChange={e => handleImageConfigChange("x", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName="Y" col={1}>
        <InputNumber
          value={config?.imageConfig?.y}
          onChange={e => handleImageConfigChange("y", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={`X ${T("word.size")}`} col={1}>
        <InputNumber
          value={config?.imageConfig?.sizex}
          onChange={e => handleImageConfigChange("sizex", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={`Y ${T("word.size")}`} col={1}>
        <InputNumber
          value={config?.imageConfig?.sizey}
          onChange={e => handleImageConfigChange("sizey", e.value)}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("word.opacity")} col={1}>
        <InputNumber
          value={config?.imageConfig?.opacity}
          onChange={e => handleImageConfigChange("opacity", e.value)}
          maxFractionDigits={2}
        />
      </InputLabelWrapper>
      <Dialog
        onHide={() => setIsIdentifiersEditDialogVisible(false)}
        visible={isIdentifiersEditDialogVisible}
        header={T("action.edit-identifiers")}
        style={{ width: "min(100vw, 1000px)", height: "400px" }}
      >
        <ChartIdentifiersEditor
          currentConfig={yAxisIdentifierValue}
          setCurrentConfig={identifiers => handleChange("yAxisIdentifier", identifiers)}
        />
      </Dialog>
    </div>
  );
};

export default DatasetScatterChartEditor;
